import React, { useState, useEffect } from 'react';
import PaymentsAPI from 'api/PaymentsAPI';
import CircularProgress from '@mui/material/CircularProgress';
import { Modal } from '@mui/material';
import PreviewInvoice from './PreviewInvoice';
import { PDFViewer } from '@react-pdf/renderer';
import
{
  standardMeterPriceGroup,
  standardMeterPriceGroupAnnual,
  proMeterPriceGroup,
  proMeterPriceGroupAnnual,
  entMeterPriceGroup,
  entMeterPriceGroupAnnual
} from 'utils/meterPriceGroups';

interface ISubMgmtModal {
  setMgmtOpen: any;
  mgmtOpen: any;
  subInfo: any;
  cusObj: any;
  planState: any;
  changePlan: any;
  newPlan: string;
  cusId: string;
  planUpdating: boolean;
  methods: any;
  createSubscription: any;
  cur_user: any;
  downgrade: boolean;
  currentPlan: any;
  setPlanUpdating: any;
  activeBtn: string;
}

const SubMgmtModal: React.FC<ISubMgmtModal> = ({
  downgrade,
  cur_user,
  createSubscription,
  methods,
  mgmtOpen,
  subInfo,
  planUpdating,
  cusId,
  changePlan,
  newPlan,
  setMgmtOpen,
  planState,
  currentPlan,
  setPlanUpdating,
  activeBtn
}) => {
  const [ incomingChanges, setIncomingChanges ] = useState<any>({ isLoading: true, data: {} });
  const [ previewOpen, setPreviewOpen ] = useState(false);

  const downgradeSubscription = async(sub_id: string, subData: any): Promise<any> => {
    setPlanUpdating(true);
    let newMetadata = {};
    let newMeterPriceGroup: any = {};
    if (newPlan === 'Standard') {
      newMetadata = {
        moduleName: 'BITE Standard',
        type: 'fixed'
      };
      newMeterPriceGroup = activeBtn === 'monthly' ? standardMeterPriceGroup : standardMeterPriceGroupAnnual;
    }
    if (newPlan === 'Pro') {
      newMetadata = {
        moduleName: 'BITE Pro',
        type: 'fixed'
      };
      newMeterPriceGroup = activeBtn === 'monthly' ? proMeterPriceGroup : proMeterPriceGroupAnnual;
    }
    if (newPlan === 'Enterprise') {
      newMetadata = {
        moduleName: 'BITE Enterprise',
        type: 'fixed'
      };
      newMeterPriceGroup = activeBtn === 'monthly' ? entMeterPriceGroup : entMeterPriceGroupAnnual;
    }
    const oldMeterPriceGroup = {
      people: {
        price: subInfo.items.data[1].price.id,
        metadata: {
          type: 'meter',
          moduleName: 'People'
        }
      },
      products: {
        price: subInfo.items.data[2].price.id,
        metadata: {
          type: 'meter',
          moduleName: 'Products'
        }
      },
      forms: {
        price: subInfo.items.data[3].price.id,
        metadata: {
          type: 'meter',
          moduleName: 'Forms'
        }
      }
    };
    subData.oldMeterPriceGroup = oldMeterPriceGroup;
    subData.newMeterPriceGroup = newMeterPriceGroup;
    subData.newMetadata = newMetadata;
    subData.oldMetadata = subInfo.items.data[0].metadata;
    const downgradeSub = await PaymentsAPI.createSubSchedule(sub_id, subData);
    if (downgradeSub?.success) {
      setPlanUpdating(false);
      history.go(0);
    }
  };

  const previewProration = async(cus_id: string, price_id: string, quantity: any, sub_id?: string) => {
    setIncomingChanges({ isLoading: true, data: {} });
    let meterPayload: any = {};
    if (incomingChanges.isLoading) {
      if (sub_id) {
        if (newPlan === 'Standard') {
          meterPayload = activeBtn === 'monthly' ? standardMeterPriceGroup : standardMeterPriceGroupAnnual;
        }
        if (newPlan === 'Pro') {
          meterPayload = activeBtn === 'monthly' ? proMeterPriceGroup : proMeterPriceGroupAnnual;
        }
        if (newPlan === 'Enterprise') {
          meterPayload = activeBtn === 'monthly' ? entMeterPriceGroup : entMeterPriceGroupAnnual;
        }
        const subChange = await PaymentsAPI.previewSubChange(
          sub_id,
          cus_id,
          price_id,
          quantity,
          meterPayload
        );
        if (subChange?.success) {
          setIncomingChanges({ isLoading: false, data: subChange?.result });
        }
      } else {
        const checkoutSub = await PaymentsAPI.previewCheckout(
          cus_id,
          price_id,
          quantity
        );
        if (checkoutSub?.success) {
          setIncomingChanges({ isLoading: false, data: checkoutSub?.result });
        }
      }
    }
  };

  const calculateDueToday = (priceChanges: any) => {
    let price;
    if (downgrade) {
      price = '0.00';
      return price;
    }
    if (priceChanges.data.lines.data.length === 6) {
      price = priceChanges.data.lines.data[priceChanges.data.lines.data.length - 5].amount + priceChanges.data.lines.data[0].amount;
    }
    if (priceChanges.data.lines.data.length === 5) {
      price = priceChanges.data.lines.data[priceChanges.data.lines.data.length - 4].amount + priceChanges.data.lines.data[0].amount;
    }
    if (priceChanges.data.lines.data.length === 1) {
      // price = priceChanges.data.lines.data[0].amount;
      price = priceChanges.data.amount_due;
    }
    // apply customer's credit balance if any
    if (priceChanges.data.starting_balance !== 0) {
      price = price + priceChanges.data.starting_balance;
    }
    price = price / 100;
    if (price < 0) {
      return '0.00';
    }
    price = price.toFixed(2);
    return price;
  };

  useEffect(() => {
    if (subInfo && subInfo.status !== 'trialing') {
      previewProration(
        cusId,
        planState.data.selectedPrice.priceId,
        planState.data.quantity,
        subInfo?.id
      );
    } else {
      previewProration(
        cusId,
        planState.data.selectedPrice.priceId,
        planState.data.quantity,
      );
    }
  }, []);

  return (
    <div className='sub-mgmt-container'>
      {!incomingChanges.isLoading ? (
        <>
          <div className='sub-mgmt-header'>
            <div className='sub-mgmt-header-content'>
              <div className='sub-mgmt-header-icon'>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                  <path d='M22 10H2M11 19L18.8 19C19.9201 19 20.4802 19 20.908 18.782C21.2843 18.5903 21.5903 18.2843 21.782 17.908C22 17.4802 22 16.9201 22 15.8V8.2C22 7.0799 22 6.51984 21.782 6.09202C21.5903 5.7157 21.2843 5.40974 20.908 5.21799C20.4802 5 19.9201 5 18.8 5H17M11 19L13 21M11 19L13 17M7 19H5.2C4.07989 19 3.51984 19 3.09202 18.782C2.71569 18.5903 2.40973 18.2843 2.21799 17.908C2 17.4802 2 16.9201 2 15.8V8.2C2 7.0799 2 6.51984 2.21799 6.09202C2.40973 5.71569 2.7157 5.40973 3.09202 5.21799C3.51984 5 4.0799 5 5.2 5H13M13 5L11 7M13 5L11 3' stroke='#4895EF' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                </svg>
              </div>
              {mgmtOpen.action === 'update' ? (
                <div className='sub-mgmt-header-text-group'>
                  <div className='sub-mgmt-header-primary-text'>
                    Review updates
                  </div>
                  <div className='sub-mgmt-header-secondary-text'>
                    Review and confirm your account plan updates.
                  </div>
                </div>
              ) : (
                <div className='sub-mgmt-header-text-group'>
                  <div className='sub-mgmt-header-primary-text'>
                    Confirm checkout
                  </div>
                  <div className='sub-mgmt-header-secondary-text'>
                    Review and confirm your account plan selection.
                  </div>
                </div>
              )}
            </div>
            <div className='sub-mgmt-header-close' onClick={() => setMgmtOpen({ open: false, action: 'update' })}>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                <path d='M18 6L6 18M6 6L18 18' stroke='#BDBEC1' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
            </div>
          </div>
          <div className='sub-mgmt-body'>
            <div className='sub-mgmt-table'>
              <div className='sub-mgmt-table-body'>
                <div className='sub-mgmt-table-column'>
                  <div className='sub-mgmt-table-cell'>
                    <div className='sub-mgmt-table-cell-text'>
                      Plan
                    </div>
                  </div>
                  <div className='sub-mgmt-table-cell'>
                    <div className='sub-mgmt-table-cell-text'>
                      Seats
                    </div>
                  </div>
                  <div className='sub-mgmt-table-cell'>
                    <div className='sub-mgmt-table-cell-text'>
                      Due Today
                    </div>
                  </div>
                </div>
                <div className='sub-mgmt-table-column'>
                  <div className='sub-mgmt-table-cell'>
                    <div className='sub-mgmt-table-cell-text-right'>
                      {newPlan} - ${planState?.data?.selectedPrice?.amount}/{planState?.data?.selectedPrice?.interval}
                    </div>
                  </div>
                  <div className='sub-mgmt-table-cell'>
                    <div className='sub-mgmt-table-cell-text-right'>
                      x {planState.data.quantity}
                    </div>
                  </div>
                  {!incomingChanges.isLoading && (
                    <div className='sub-mgmt-table-cell'>
                      <div className='sub-mgmt-table-cell-text-right'>
                        ${calculateDueToday(incomingChanges)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='preview-invoice-btn' onClick={() => setPreviewOpen(true)}>
              Preview invoice
            </div>
          </div>
          <div className='sub-mgmt-ovw'>
            <div className='sub-mgmt-ovw-box'>
              <div className='sub-mgmt-ovw-box-text'>
                Plan Price
              </div>
              <div className='sub-mgmt-ovw-box-price'>
                {/* ${(incomingChanges?.data?.amount_due / 100).toFixed(2)}
                /{planState?.data?.selectedPrice?.interval} */}
                ${
                  planState?.data?.selectedPrice?.amount *
                  planState?.data?.quantity
                }
                /
                {planState?.data?.selectedPrice?.interval}
              </div>
              <div className='sub-mgmt-ovw-box-msg'>
                Tax not included
              </div>
            </div>
            <div className='sub-mgmt-ovw-box'>
              <div className='sub-mgmt-ovw-box-text'>
                Due Today
              </div>
              <div className='sub-mgmt-ovw-box-price'>
                ${calculateDueToday(incomingChanges)}
              </div>
              <div className='sub-mgmt-ovw-box-msg'>
                Then $
                {
                  planState?.data?.selectedPrice?.amount *
                  planState?.data?.quantity
                }
                /
                {planState?.data?.selectedPrice?.interval}
              </div>
            </div>
          </div>
          <div className='sub-mgmt-footer'>
            <div className='sub-mgmt-actions'>
              <div className='sub-mgmt-cancel-btn' onClick={() => setMgmtOpen({ open: false, action: 'update' })}>
                <div className='sub-mgmt-cancel-btn-text'>
                  Cancel
                </div>
              </div>
              {mgmtOpen.action === 'checkout' && (
                <div
                  className='sub-mgmt-confirm-btn'
                  onClick={() =>
                    createSubscription(
                      cusId,
                      methods?.data?.data[0]?.id,
                      newPlan,
                      false
                    )
                  }
                >
                  {planUpdating ? (
                    <div>
                      <CircularProgress style={{ color: '#FFF', marginTop: '5px' }} />
                    </div>
                  ) : (
                    <div className='sub-mgmt-confirm-btn-text'>
                      Pay
                    </div>
                  )}
                </div>
              )}
              {mgmtOpen.action === 'update' && (
                <>
                  {downgrade ? (
                    <div
                      className='sub-mgmt-confirm-btn'
                      onClick={() =>
                        downgradeSubscription(
                          subInfo?.id,
                          {
                            oldPrice: currentPlan.data.selectedPrice.priceId,
                            price_id: planState.data.selectedPrice.priceId,
                            quantity: planState.data.quantity
                          }
                        )
                      }
                    >
                      {planUpdating ? (
                        <div>
                          <CircularProgress style={{ color: '#FFF', marginTop: '5px' }} />
                        </div>
                      ) : (
                        <div className='sub-mgmt-confirm-btn-text'>
                          Confirm Changes
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className='sub-mgmt-confirm-btn'
                      onClick={() =>
                        changePlan(
                          {
                            subItemId: subInfo?.items?.data[0]?.id,
                            sub_id: subInfo?.id,
                            newPrice: planState.data.selectedPrice.priceId,
                            newQuantity: planState.data.quantity,
                            peopleItemId: subInfo?.items?.data[1]?.id,
                            productsItemId: subInfo?.items?.data[2]?.id,
                            formsItemId: subInfo?.items?.data[3]?.id,
                          }
                        )
                      }
                    >
                      {planUpdating ? (
                        <div>
                          <CircularProgress style={{ color: '#FFF', marginTop: '5px' }} />
                        </div>
                      ) : (
                        <div className='sub-mgmt-confirm-btn-text'>
                          Confirm Changes
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        <div>
          <CircularProgress style={{ color: '#0E8BFF' }} />
        </div>
      )}
      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      >
        <div className='preview-invoice-container'>
          <PDFViewer>
            <PreviewInvoice cur_user={cur_user} newPlan={newPlan} planState={planState} />
          </PDFViewer>
        </div>
      </Modal>
    </div>
  );
};

export default SubMgmtModal;
